
import { PercentPipe } from '@angular/common';
import { Mention } from '../../../Mentions/models/mention';

const percentPipe = new PercentPipe('en-US');

const THOUSAND = 10 ** 3;
const MILLION = 10 ** 6;
const BILLION = 10 ** 9;
const TRILLION = 10 ** 12;
const QUADRILLION = 10 ** 15;
const QUINTILLION = 10 ** 18;

/* Tiers hold the rules for how numbers get transformed */
interface Tier {
    name: string;
    min: number;
    max: number;
    transform: (val: number, digits: number) => string;
}

const TIERS: Tier[] = [
    { name: 'hundreds', min: 1, max: 999, transform: (val, digits) => val.toFixed(digits) },
    { name: 'thousands', min: THOUSAND, max: MILLION - 1, transform: (val, digits) => (val / THOUSAND).toFixed(digits) + 'K' },
    { name: 'millions', min: MILLION, max: BILLION - 1, transform: (val, digits) => (val / MILLION).toFixed(digits) + 'M' },
    { name: 'billions', min: BILLION, max: TRILLION - 1, transform: (val, digits) => (val / BILLION).toFixed(digits) + 'B' },
    { name: 'trillions', min: TRILLION, max: QUADRILLION - 1, transform: (val, digits) => (val / TRILLION).toFixed(digits) + 'T' },
    { name: 'quadrillions', min: QUADRILLION, max: QUINTILLION - 1, transform: (val, digits) => (val / QUADRILLION).toFixed(digits) + 'Q' },
];

// DOCS: https://agility-confluence.atlassian.net/wiki/spaces/AM/pages/660753/Number+Abbreviations
export function abbreviateNumber(value: unknown, args?: { decimals: number }): string {
    // coerce input into a number
    const n = typeof value === 'number' ? value : Number(value);

    // capture the value of decimal places to use, else set default to 1
    const decimalPlaces = args?.decimals ?? 1;

    // guard against unworkable values
    if (!Number.isFinite(n)) {
        if (n === Infinity || n === -Infinity) {
            return '∞';
        };
        return String(value); // for NaN or non-coercible values
    }

    // between 0 and 1 show three decimals
    if (n >= 0 && n <= 1) {
        return (Math.round(n * 1000) / 1000).toString();
    }

    // no decimals for numbers under 1000
    if (n > -THOUSAND && n < THOUSAND) {
        return n.toFixed(args?.decimals ?? 0); // use zero unless decimals are specifically passed in
    }

    // transform based on tier
    for (const tier of TIERS) {
        if (n >= tier.min && n <= tier.max) {
            return tier.transform(n, decimalPlaces);
        }
    }

    // value larger than highest tier (Q) aka too large, is formatted in scientific notation
    return n.toExponential(args?.decimals ?? 0);
}

export function numberWithSeparators(x: number, separator: string = ','): string {
    return x.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
}

export function isPositiveInteger(num: number): boolean {
    // We started storing a max of 999,999,999 for impression when it was undefined,
    // so here we have to check Mention.maxAudienceReach to treat it as undefined
    return !isNaN(num) && num !== null && num !== Mention.maxAudienceReach && num !== 0;
}

export function showZeroToTwoDecimal(value: number): string {
    if (isNaN(value)) {
        return 'N/A';
    }

    return percentPipe.transform(value || 0, '1.0-2');
}
